/**
 * @voltxampere/engine — Web Worker entry point (Sprint 9)
 *
 * This file is designed to be loaded via Vite's worker import
 * (`new Worker(new URL("./engine-worker.js", import.meta.url))`).
 * It wires the global `self.onmessage` to the transport-agnostic
 * handler and sets up a single mutable state container.
 *
 * The file is skipped by the Node-only test runner because the
 * `self` global is not available. Tests import `./handler.ts` and
 * `./state.ts` directly.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { createHandlerDeps, handleRequest } from "./handler.js";
import { EngineRequestSchema } from "./protocol.js";
function isWorkerGlobal(g) {
    return (typeof g === "object" &&
        g !== null &&
        typeof g.postMessage === "function");
}
export function bootWorker() {
    const scope = globalThis;
    if (!isWorkerGlobal(scope)) {
        throw new Error("VoltXAmpere worker entry requires a DedicatedWorkerGlobalScope-like global");
    }
    const post = (msg) => {
        scope.postMessage(msg);
    };
    const deps = createHandlerDeps(post);
    scope.onmessage = (ev) => {
        const parsed = EngineRequestSchema.safeParse(ev.data);
        if (!parsed.success) {
            const id = typeof ev.data === "object" &&
                ev.data !== null &&
                typeof ev.data.id === "string"
                ? ev.data.id
                : "00000000-0000-0000-0000-000000000000";
            post({
                type: "error",
                id,
                error: {
                    code: "INVALID_REQUEST",
                    message: `Invalid request shape: ${parsed.error.message}`,
                },
            });
            return;
        }
        void handleRequest(parsed.data, deps);
    };
}
// Auto-boot when loaded inside a worker. Tests that import this
// module in Node won't hit this branch because `self` is absent.
if (typeof self !== "undefined" &&
    typeof self.postMessage === "function") {
    bootWorker();
}
//# sourceMappingURL=entry.js.map